import { createFileRoute, Link } from '@tanstack/react-router'

export const Route = createFileRoute('/privacy')({
  component: PrivacyPage,
})

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-10">
      <h2 className="text-lg font-bold text-gray-900 mb-3 pb-2 border-b border-gray-100">{title}</h2>
      <div className="space-y-3 text-sm text-gray-600 leading-relaxed">{children}</div>
    </section>
  )
}

function Sub({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="font-semibold text-gray-800 mb-1">{title}</p>
      {children}
    </div>
  )
}

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Nav */}
      <header className="sticky top-0 z-50 border-b border-gray-100 bg-white/80 backdrop-blur">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="DistriHub" className="w-7 h-7 object-contain" />
            <span className="text-base font-bold tracking-tight">Distri<span className="text-rose-600">Hub</span></span>
          </Link>
          <Link to="/login" className="text-sm text-gray-600 hover:text-gray-900 font-medium">Sign in</Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 sm:px-6 py-14">
        <div className="mb-10">
          <p className="text-xs font-semibold uppercase tracking-widest text-rose-600 mb-2">Legal</p>
          <h1 className="text-3xl font-extrabold text-gray-900">Privacy Policy</h1>
          <p className="mt-2 text-sm text-gray-500">
            Jiamini Innovations · Last updated: 30 July 2026
          </p>
        </div>

        <Section title="1. Who we are">
          <p>
            DistriHub is a product of <strong>Jiamini Innovations</strong>, a company registered in Kenya.
            When we say "DistriHub", "we", "us", or "our" in this policy, we mean Jiamini Innovations.
          </p>
          <p>
            We operate two products covered by this policy:
          </p>
          <ul className="list-disc ml-5 space-y-1">
            <li><strong>DistriHub ERP</strong> — a cloud-based business management platform for distributors, retailers, and field sales teams (app.distrihub.co.ke).</li>
            <li><strong>DistriMarket</strong> — a social commerce marketplace that connects DistriHub businesses with their customers (available as a mobile app).</li>
          </ul>
          <p>
            For questions about this policy, contact us at{' '}
            <a href="mailto:info@distrihub.co.ke" className="text-rose-600 hover:underline">info@distrihub.co.ke</a>.
          </p>
        </Section>

        <Section title="2. Data we collect and why">
          <Sub title="DistriHub ERP — Business accounts">
            <p>When a business registers and uses the ERP, we collect:</p>
            <ul className="list-disc ml-5 mt-1 space-y-1">
              <li><strong>Account information:</strong> business name, owner name, email address, phone number, business type, and logo.</li>
              <li><strong>Operational data:</strong> products, inventory levels, sales records, purchase orders, supplier details, and customer accounts you create within the system.</li>
              <li><strong>Staff and payroll data:</strong> employee names, ID numbers, employment details, salary structures, leave records, loans, and payslips. This data belongs to you — we process it strictly on your behalf.</li>
              <li><strong>Financial data:</strong> journal entries, expense records, petty cash transactions, and accounting reports.</li>
              <li><strong>Payment data:</strong> M-Pesa transaction references for payments made through or recorded in the platform. We do not store full M-Pesa credentials or PINs.</li>
              <li><strong>Location data:</strong> branch addresses and GPS coordinates where you choose to provide them.</li>
              <li><strong>Usage data:</strong> login timestamps, IP addresses, browser/device information, and actions taken within the platform (our audit log).</li>
            </ul>
          </Sub>

          <Sub title="DistriHub ERP — Staff and employee portal">
            <p>Staff members invited to the ERP or employee portal have the following data processed:</p>
            <ul className="list-disc ml-5 mt-1 space-y-1">
              <li>Name, email address, phone number, and profile photo.</li>
              <li>Role assignments, branch assignments, and permission levels.</li>
              <li>Employment records, contracts, leave applications, appraisals, loan records, and payslips — all entered or authorised by the employing business.</li>
              <li>Field agent location data (GPS breadcrumbs) when the DistriHub Field mobile app is in active use during a van dispatch. This data is used solely for route management and reconciliation.</li>
            </ul>
            <p className="mt-1">
              The employing business is the data controller for their staff records. Jiamini Innovations acts as a data processor for this data and processes it only under the instructions of the business.
            </p>
          </Sub>

          <Sub title="DistriMarket — Business sellers">
            <p>
              When a business activates their DistriMarket storefront through the ERP, we additionally process:
            </p>
            <ul className="list-disc ml-5 mt-1 space-y-1">
              <li>Product listings, prices, photos, and descriptions you publish.</li>
              <li>Promotional campaigns, flash sales, and group buy configurations.</li>
              <li>Orders received through DistriMarket and the associated customer details.</li>
              <li>Business post and story content you publish on the marketplace feed.</li>
              <li>Seller analytics: views, engagement rates, and conversion data on your listings.</li>
            </ul>
          </Sub>

          <Sub title="DistriMarket — Consumers (mobile app)">
            <p>
              When a consumer uses the DistriMarket mobile app, we collect:
            </p>
            <ul className="list-disc ml-5 mt-1 space-y-1">
              <li>Name, email address, and phone number at registration.</li>
              <li>Location data to show nearby businesses and enable delivery (with your permission).</li>
              <li>Browsing and purchasing history to personalise your feed and product recommendations.</li>
              <li>Order and payment details, including M-Pesa transaction references.</li>
              <li>Device tokens for push notification delivery.</li>
              <li>Content interactions: posts viewed, products liked, businesses followed.</li>
            </ul>
          </Sub>
        </Section>

        <Section title="3. Legal basis for processing">
          <p>Under the Kenya Data Protection Act 2019, we rely on the following lawful bases:</p>
          <ul className="list-disc ml-5 space-y-1">
            <li><strong>Performance of a contract</strong> — to deliver the services you have signed up for.</li>
            <li><strong>Consent</strong> — for marketing communications and non-essential location tracking. You may withdraw consent at any time.</li>
            <li><strong>Legitimate interests</strong> — for security monitoring, fraud prevention, service improvement, and audit logging, where these do not override your rights.</li>
            <li><strong>Legal obligation</strong> — where we are required to process data to comply with Kenyan law, including KRA eTIMS reporting obligations.</li>
          </ul>
        </Section>

        <Section title="4. How we use your data">
          <ul className="list-disc ml-5 space-y-1">
            <li>To provide, operate, and maintain the DistriHub ERP and DistriMarket platforms.</li>
            <li>To process transactions and send related notices (receipts, invoices, payment confirmations).</li>
            <li>To generate payslips, calculate statutory deductions, and support eTIMS compliance on your behalf.</li>
            <li>To personalise the DistriMarket feed and surface relevant products and businesses to consumers.</li>
            <li>To send service notifications — billing alerts, security alerts, and product updates.</li>
            <li>To detect, prevent, and investigate fraud, security incidents, and policy violations.</li>
            <li>To improve our platform through aggregated, anonymised usage analytics.</li>
            <li>To respond to your support requests.</li>
          </ul>
          <p>We do not sell your data to third parties. We do not use your data for advertising outside of the DistriMarket feed.</p>
        </Section>

        <Section title="5. Who we share data with">
          <p>We share data only where necessary to operate our services:</p>
          <ul className="list-disc ml-5 space-y-1">
            <li><strong>Supabase</strong> — our primary database infrastructure. Data is stored on Supabase-managed PostgreSQL servers.</li>
            <li><strong>Railway</strong> — our application hosting provider.</li>
            <li><strong>Cloudflare</strong> — CDN and web application delivery for our frontend.</li>
            <li><strong>Resend</strong> — transactional email delivery (receipts, notifications, verification emails).</li>
            <li><strong>Safaricom M-Pesa</strong> — to facilitate mobile money payments where enabled.</li>
            <li><strong>Kenya Revenue Authority (KRA)</strong> — eTIMS integration sends sales data to KRA on behalf of businesses that have enabled this feature. We transmit only what you are legally required to report.</li>
          </ul>
          <p>
            All our sub-processors are bound by data processing agreements and are required to maintain appropriate security standards.
          </p>
          <p>
            We may disclose data where required by Kenyan law, a court order, or a lawful request by a government authority.
          </p>
        </Section>

        <Section title="6. Data retention">
          <ul className="list-disc ml-5 space-y-1">
            <li><strong>Active accounts:</strong> we retain your data for as long as your account is active.</li>
            <li><strong>After account closure:</strong> we retain business and financial records for 7 years to comply with the Kenya Companies Act and tax obligations. Staff and payroll records are retained for 5 years after the employment relationship ends.</li>
            <li><strong>Consumer accounts:</strong> DistriMarket consumer data is retained for as long as the account is active, plus 2 years after the last transaction.</li>
            <li><strong>GPS breadcrumbs:</strong> field agent location pings are retained for 90 days and then automatically deleted.</li>
            <li><strong>Audit logs:</strong> retained for 2 years.</li>
          </ul>
          <p>You may request earlier deletion of certain data where permitted by law — see Section 8.</p>
        </Section>

        <Section title="7. Data security">
          <p>
            We take security seriously. Our measures include:
          </p>
          <ul className="list-disc ml-5 space-y-1">
            <li>All data is encrypted in transit using TLS 1.2 or higher.</li>
            <li>Passwords are hashed and never stored in plain text.</li>
            <li>Access to production systems is restricted to authorised personnel only.</li>
            <li>JWT tokens are short-lived (30 minutes) and refresh tokens are rotated and blacklisted on each use.</li>
            <li>Role-based access control limits what each user can see and do within the ERP.</li>
            <li>An audit log records all significant actions within each business account.</li>
          </ul>
          <p>
            No system is perfectly secure. If you suspect a security incident involving your account, contact us immediately at{' '}
            <a href="mailto:info@distrihub.co.ke" className="text-rose-600 hover:underline">info@distrihub.co.ke</a>.
          </p>
        </Section>

        <Section title="8. Your rights under the Data Protection Act 2019">
          <p>As a data subject under Kenyan law, you have the right to:</p>
          <ul className="list-disc ml-5 space-y-1">
            <li><strong>Access</strong> — request a copy of the personal data we hold about you.</li>
            <li><strong>Correction</strong> — ask us to correct inaccurate or incomplete personal data.</li>
            <li><strong>Deletion</strong> — request deletion of your personal data where we no longer have a lawful basis to hold it.</li>
            <li><strong>Portability</strong> — receive your data in a structured, commonly used format.</li>
            <li><strong>Objection</strong> — object to processing based on legitimate interests.</li>
            <li><strong>Withdraw consent</strong> — where processing is based on consent, withdraw it at any time without affecting prior processing.</li>
          </ul>
          <p>
            To exercise any of these rights, email us at{' '}
            <a href="mailto:info@distrihub.co.ke" className="text-rose-600 hover:underline">info@distrihub.co.ke</a>{' '}
            with the subject line "Data Rights Request". We will respond within 21 days as required by the DPA 2019.
          </p>
          <p>
            If you are a staff member of a DistriHub business, your employer (the data controller) is the first point of contact for your employment records. We will coordinate with them to fulfil your request.
          </p>
        </Section>

        <Section title="9. Cookies and local storage">
          <p>
            The DistriHub ERP web application uses browser local storage to maintain your login session and UI preferences (theme, sidebar state). We do not use third-party advertising cookies. No cookie consent banner is required for strictly necessary session storage.
          </p>
        </Section>

        <Section title="10. Changes to this policy">
          <p>
            We may update this policy as our services evolve or as law requires. We will notify active users of material changes by email and post the updated policy at this URL with a new effective date. Continued use of the platform after the effective date constitutes acceptance of the revised policy.
          </p>
        </Section>

        <Section title="11. Contact us">
          <p>
            For any privacy-related questions, requests, or complaints:
          </p>
          <div className="bg-gray-50 rounded-xl p-4 text-sm">
            <p className="font-semibold text-gray-800">Jiamini Innovations</p>
            <p>Product: DistriHub</p>
            <p>Email: <a href="mailto:info@distrihub.co.ke" className="text-rose-600 hover:underline">info@distrihub.co.ke</a></p>
            <p>Country: Kenya</p>
          </div>
          <p>
            If you are not satisfied with our response, you have the right to lodge a complaint with the{' '}
            <strong>Office of the Data Protection Commissioner (ODPC)</strong> of Kenya at{' '}
            <a href="https://www.odpc.go.ke" target="_blank" rel="noopener noreferrer" className="text-rose-600 hover:underline">www.odpc.go.ke</a>.
          </p>
        </Section>
      </main>

      <footer className="border-t border-gray-100 py-6">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-400">
          <span>© {new Date().getFullYear()} Jiamini Innovations. All rights reserved.</span>
          <div className="flex items-center gap-4">
            <Link to="/terms" className="hover:text-gray-600">Terms &amp; Conditions</Link>
            <Link to="/" className="hover:text-gray-600">Back to home</Link>
          </div>
        </div>
      </footer>
    </div>
  )
}
